import { useEffect, useState } from 'react';
import { PersonIcon } from '@radix-ui/react-icons';
import { Avatar } from '@producer/ui-kit';
import styled from 'styled-components';

import { context } from '../../app/context';
import {
  Member,
  ProjectPriority,
  ProjectPriorityByValue,
  Step,
  Task,
  TaskLevel,
  TaskStatus,
} from '../../app/entities';
import { PriorityIcon } from '../process/components/priority-selector';
import { StatusSelector } from '../process/components/status-selector';
import { CustomAvatar } from './components/styled-tasks-components';
import { taskGroupingStore } from '../../core/stores/task-store';

export type Group = {
  name: string;
  value: any;
  tasks: Task[];
};

export type SortedGroups = {
  [key: string]: Group;
};

export interface GroupByOption {
  key: string;
  label: string;
  defaultKey?: any;
  defaultValue?: string;
  getValues?: () => any;
  getDefaultValue: (value: any) => any;
  getTitle: (task: Task) => string;
  getIcon?: (value: any) => any;
  sortBy: (a: Group, b: Group) => any;
}

const baseTaskOptions: GroupByOption[] = [
  {
    key: 'status',
    defaultValue: TaskStatus.Todo,
    label: 'Status',
    getTitle: (task: Task) => Task.StatusMapper[task.status].title,
    getDefaultValue: (status: string) => ({ status }),
    getIcon: (value: TaskStatus) => (
      <StatusSelector status={value} disableTooltip isViewOnly statusMap={Task.StatusMapper} />
    ),
    sortBy: (a: Group, b: Group) => {
      if (!a.value) return -1;
      if (!b.value) return 1;
      return a.value > b.value ? -1 : 1;
    },
  },
  {
    key: 'assigneeId',
    label: 'Assignee',
    defaultKey: 'no-assignee',
    defaultValue: undefined,
    getDefaultValue: (assigneeId: string) => ({ assigneeId }),
    getTitle: (task: Task) => task.assignee?.fullName ?? 'No assignee',
    getIcon: (assigneeId: string) => {
      const assignee = Member.getOne(assigneeId)! as Member;

      if (!assignee) {
        return (
          <AvatarWrapper>
            <CustomAvatar>
              <PersonIcon />
            </CustomAvatar>
          </AvatarWrapper>
        );
      }

      return (
        <PrioritySelectorWrapper>
          <Avatar size="small" theme="dark" initials={assignee.initials} src={assignee.avatar} />
        </PrioritySelectorWrapper>
      );
    },
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
  {
    key: 'priority',
    label: 'Priority',
    defaultValue: '',
    getDefaultValue: (priority: string) => ({ priority }),
    getTitle: (task: Task) =>
      task.priority ? ProjectPriorityByValue[task.priority] : 'No priority',
    getIcon: (value: ProjectPriority) => (
      <PrioritySelectorWrapper>
        <PriorityIcon priority={value} />
      </PrioritySelectorWrapper>
    ),
    sortBy: (a: Group, b: Group) => {
      if (!a.value) return -1;
      if (!b.value) return 1;
      return a.value > b.value ? -1 : 1;
    },
  },
  {
    key: 'tag',
    label: 'Tag',
    defaultValue: '',
    getValues: () => {
      const space = context.space;
      return space.tags?.filter((tag) => tag.target === 'task');
    },
    getDefaultValue: (tagId: string) => ({ tagsIds: [tagId] }),
    getTitle: (task: Task) => (task.priority ? ProjectPriorityByValue[task.priority] : 'No tag'),
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

const projectTaskOptions: GroupByOption[] = [
  ...baseTaskOptions,
  {
    key: 'stepId',
    label: 'Step',
    defaultKey: 'no-step',
    defaultValue: undefined,
    getDefaultValue: (stepId: string) => {
      const step = Step.getOne(stepId);

      return {
        stepId,
        dueDate: step?.dueDate,
      };
    },
    getTitle: (task: Task) => task.step?.name ?? 'No step',
    getIcon: () => null,
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

const spaceTaskOptions: GroupByOption[] = [
  ...baseTaskOptions,
  {
    key: 'projectId',
    label: 'Project',
    defaultKey: 'no-project',
    defaultValue: undefined,
    getDefaultValue: (projectId: string) => ({ projectId }),
    getTitle: (task: Task) => task.project?.name ?? 'No project',
    getIcon: () => null,
    sortBy: (a: Group, b: Group) => {
      if (b.value === undefined) return -1;
      if (a.value === undefined) return 1;
      return a.name.localeCompare(b.name);
    },
  },
];

/**
 * Returns the grouping options available for the given task level.
 */
export function getTaskGroupingOptions(taskLevel: TaskLevel): GroupByOption[] {
  return taskLevel === 'space' ? spaceTaskOptions : projectTaskOptions;
}

/**
 * Returns the currently selected grouping option for the given task level,
 * falling back to the first option (status) when the stored key is not valid
 * for this level.
 *
 * Reads `taskGroupingStore.groupBy`, so must be called during the render of an
 * `observer` component to stay reactive.
 */
export function getActiveGroupBy(taskLevel: TaskLevel): GroupByOption {
  const options = getTaskGroupingOptions(taskLevel);
  return options.find((option) => option.key === taskGroupingStore.groupBy) || options[0];
}

/**
 * Builds the sorted groups for the given tasks using the active grouping option.
 * Shared by the list and board views so they group identically.
 *
 * NOTE: this returns only groups that contain at least one task (matching the
 * historical list behaviour). The board enumerates the full column set
 * separately via `getTaskColumns`.
 */
export function buildTaskGroups(tasks: Task[], groupBy: GroupByOption): SortedGroups {
  const groups: SortedGroups = {};

  if (groupBy.getValues) {
    /**
     * Only works for tags atm
     */
    groupBy.getValues().forEach((groupValue: any) => {
      groups[groupValue._id] = {
        name: groupValue.text,
        value: groupValue._id,
        tasks: tasks.filter((task) => task.tagsIds.includes(groupValue._id)),
      };
    });

    if (tasks.some((task) => task.tagsIds.length === 0)) {
      groups['no-tag'] = {
        name: 'No tag',
        value: undefined,
        tasks: tasks.filter((task) => task.tagsIds.length === 0),
      };
    }
  } else {
    tasks.forEach((task) => {
      const groupByKey = (task[groupBy.key as keyof Task] ||
        groupBy.defaultKey) as keyof typeof groups;

      const value = (task[groupBy.key as keyof Task] ||
        groupBy.defaultValue) as keyof typeof groups;

      if (!groups[groupByKey]) {
        groups[groupByKey] = {
          name: groupBy.getTitle(task),
          value,
          tasks: [],
        };
      }

      groups[groupByKey].tasks.push(task);
    });
  }

  return groups;
}

/**
 * Hook returning the active grouping option and the (non-empty) sorted groups
 * for the given tasks. Shared between the list and (indirectly) the board.
 */
export function useTaskGroups(tasks: Task[], taskLevel: TaskLevel) {
  const activeGroupBy = getActiveGroupBy(taskLevel);
  const [groups, setGroups] = useState<SortedGroups>();

  useEffect(() => {
    if (!tasks) return;

    setGroups(buildTaskGroups(tasks, activeGroupBy));
  }, [tasks, activeGroupBy]);

  return { activeGroupBy, groups };
}

const PrioritySelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  width: 2.4rem;
  height: 2.4rem;
  margin-inline-end: 0.8rem;
  background-color: #25272d;
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0.8rem;
`;
